import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatPrice, formatFlightPrice, getCurrency, initializeCurrency } from './utils/currency';
import { getBaggageAllowance, formatBaggageForDisplay, airlineBaggageDatabase } from './utils/airlineBaggageDatabase';
import './FlightDetailsPage.css';
import './FlightDetailsPageFix.css';
import './BaggageAllowanceEnhanced.css';

const FlightDetailsPage = () => {
  const location = useLocation();
  const flight = location.state?.flight;
  const searchData = location.state?.searchData;
  
  // Get the searched travel class from searchData
  const initialTravelClass = searchData?.travelClass || flight?.travelClass || flight?.cabinClass || 'economy';
  
  // State to track selected class (can be changed on this page)
  const [selectedClass, setSelectedClass] = useState(initialTravelClass);
  
  // Debug: Log when selectedClass changes
  React.useEffect(() => {
    console.log('🔵 Selected class changed to:', selectedClass);
    console.log('🔵 Available classes:', availableClasses);
  }, [selectedClass]);
  
  // Get available classes from classPrices or default to economy and business
  const availableClasses = flight?.classPrices && Object.keys(flight.classPrices).length > 0
    ? Object.keys(flight.classPrices)
    : ['economy', 'business'];
  
  // Get current class data based on selected class
  const currentClassData = flight?.classPrices?.[selectedClass] || flight?.classPrices?.[availableClasses[0]];
  
  // Update price based on selected class
  const currentPriceObj = currentClassData?.price || flight?.price || null;
  const currentPrice = currentClassData?.price?.total || currentClassData?.price || currentClassData || flight?.price?.total || flight?.price || flight?.total;
  const currentBaseFare = currentClassData?.price?.base || flight?.price?.base || flight?.baseFare;
  const currentTaxes = currentClassData?.price?.taxes || flight?.price?.taxes || flight?.taxes;
  
  // Format the class name for display
  const formatClassForDisplay = (classValue) => {
    if (!classValue) return 'Economy';
    const classMap = {
      'economy': 'Economy',
      'business': 'Business',
      'premium_economy': 'Premium Economy',
      'first': 'First Class'
    };
    return classMap[classValue.toLowerCase()] || classValue.charAt(0).toUpperCase() + classValue.slice(1);
  };

  // Stops label from API count: 0 = Direct, 1 = 1 stop, 2+ = 2 stops
  const stopsLabel = (stops) => {
    const n = typeof stops === 'number' ? stops : parseInt(stops, 10);
    if (isNaN(n) || n < 0) return 'Direct';
    if (n === 0) return 'Direct';
    return n === 1 ? '1 stop' : `${n} stops`;
  };

  // Get stop airports from segments (API format: departure.airport/arrival.airport or raw: origin.iata_code/destination.iata_code)
  const getCityNameFromCode = (code) => {
    const cityMap = {
      'LOS': 'Lagos', 'ABV': 'Abuja', 'LHR': 'London', 'LGW': 'London', 'DXB': 'Dubai', 'AUH': 'Abu Dhabi',
      'ACC': 'Accra', 'CDG': 'Paris', 'JFK': 'New York', 'LAX': 'Los Angeles', 'FRA': 'Frankfurt', 'AMS': 'Amsterdam',
      'NBO': 'Nairobi', 'CPT': 'Cape Town', 'JNB': 'Johannesburg', 'CAI': 'Cairo', 'ADD': 'Addis Ababa',
      'IST': 'Istanbul', 'DOH': 'Doha', 'MAD': 'Madrid', 'FCO': 'Rome', 'MUC': 'Munich', 'ZRH': 'Zurich',
      'ATH': 'Athens', 'LIS': 'Lisbon', 'VIE': 'Vienna', 'BRU': 'Brussels', 'DUB': 'Dublin'
    };
    return cityMap[code] || code || 'City';
  };

  const getStopAirportsFromSegments = (segments) => {
    if (!segments || !Array.isArray(segments) || segments.length < 2) return [];
    return segments.slice(0, -1).map((seg) => {
      const code = seg.arrival?.airport || seg.destination?.iata_code || (typeof seg.destination === 'string' ? seg.destination : null);
      if (!code) return null;
      return { code: typeof code === 'string' ? code : code.iata_code || code, city: getCityNameFromCode(typeof code === 'string' ? code : code.iata_code || code) };
    }).filter(Boolean);
  };
  
  // Helper function to get airline code from airline name
  const getAirlineCodeFromName = (airlineName) => {
    if (!airlineName) return 'DEFAULT';
    const name = airlineName.toLowerCase();
    
    // Major International Airlines
    if (name.includes('british airways')) return 'BA';
    if (name.includes('emirates')) return 'EK';
    if (name.includes('ethiopian')) return 'ET';
    if (name.includes('qatar')) return 'QR';
    if (name.includes('turkish')) return 'TK';
    if (name.includes('lufthansa')) return 'LH';
    if (name.includes('air france')) return 'AF';
    if (name.includes('klm')) return 'KL';
    if (name.includes('virgin atlantic')) return 'VS';
    if (name.includes('american airlines')) return 'AA';
    if (name.includes('delta') && !name.includes('connection')) return 'DL';
    if (name.includes('united airlines')) return 'UA';
    if (name.includes('air canada')) return 'AC';
    if (name.includes('swiss')) return 'LX';
    if (name.includes('scandinavian') || name.includes('sas')) return 'SK';
    if (name.includes('aer lingus')) return 'EI';
    if (name.includes('egyptair')) return 'MS';
    if (name.includes('royal air maroc')) return 'AT';
    if (name.includes('rwandair')) return 'WB';
    if (name.includes('air malta')) return 'KM';
    if (name.includes('air transat')) return 'TS';
    if (name.includes('brussels')) return 'SN';
    if (name.includes('iberia')) return 'IB';
    if (name.includes('austrian')) return 'OS';
    if (name.includes('tap') || name.includes('portugal')) return 'TP';
    if (name.includes('lot') || name.includes('polish')) return 'LO';
    if (name.includes('saudia') || name.includes('saudi')) return 'SV';
    if (name.includes('etihad')) return 'EY';
    if (name.includes('gulf air')) return 'GF';
    if (name.includes('royal jordanian')) return 'RJ';
    if (name.includes('south african')) return 'SA';
    if (name.includes('kenya airways')) return 'KQ';
    if (name.includes('srilankan')) return 'UL';
    if (name.includes('thai airways')) return 'TG';
    if (name.includes('singapore')) return 'SQ';
    if (name.includes('cathay pacific')) return 'CX';
    if (name.includes('japan airlines')) return 'JL';
    if (name.includes('all nippon') || name.includes('ana')) return 'NH';
    if (name.includes('korean air')) return 'KE';
    if (name.includes('asiana')) return 'OZ';
    if (name.includes('china airlines')) return 'CI';
    if (name.includes('eva air')) return 'BR';
    if (name.includes('qantas')) return 'QF';
    if (name.includes('air new zealand')) return 'NZ';
    if (name.includes('latam')) return 'LA';
    if (name.includes('avianca')) return 'AV';
    if (name.includes('copa')) return 'CM';
    if (name.includes('aeromexico')) return 'AM';
    if (name.includes('air peace')) return 'P4';
    if (name.includes('arik air')) return 'W3';
    if (name.includes('ibom air')) return 'QI';
    if (name.includes('tunisair')) return 'TU';
    if (name.includes('air algérie') || name.includes('air algerie')) return 'AH';
    if (name.includes('kuwait')) return 'KU';
    if (name.includes('middle east airlines')) return 'ME';
    if (name.includes('xiamen')) return 'MF';
    if (name.includes('china eastern')) return 'MU';
    if (name.includes('air china')) return 'CA';
    if (name.includes('china southern')) return 'CZ';
    
    return 'DEFAULT';
  };
  
  // Debug spacing issues
  React.useEffect(() => {
    const checkSpacing = () => {
      const summaryCard = document.querySelector('.flight-summary-card');
      const detailsMain = document.querySelector('.flight-details-main');
      const firstDetailCard = document.querySelector('.detail-card:first-child');
      
      if (summaryCard) {
        const summaryStyles = window.getComputedStyle(summaryCard);
        const summarySection = summaryCard.closest('.flight-summary-section');
        const summarySectionStyles = summarySection ? window.getComputedStyle(summarySection) : null;
        const pricePanel = document.querySelector('.price-breakdown-panel');
        const pricePanelHeight = pricePanel ? pricePanel.offsetHeight : 0;
        console.log('🔍 Flight Summary Card:', {
          marginBottom: summaryStyles.marginBottom,
          paddingBottom: summaryStyles.paddingBottom,
          height: summaryCard.offsetHeight,
          bottom: summaryCard.getBoundingClientRect().bottom,
          sectionMarginBottom: summarySectionStyles?.marginBottom,
          sectionPaddingBottom: summarySectionStyles?.paddingBottom,
          pricePanelHeight: pricePanelHeight,
          cardHeight: summaryCard.offsetHeight
        });
      }
      
      if (detailsMain) {
        const detailsStyles = window.getComputedStyle(detailsMain);
        const detailsLayout = detailsMain.querySelector('.details-layout');
        const detailsLayoutStyles = detailsLayout ? window.getComputedStyle(detailsLayout) : null;
        const detailsContent = detailsMain.querySelector('.flight-details-content');
        const detailsContentStyles = detailsContent ? window.getComputedStyle(detailsContent) : null;
        console.log('🔍 Flight Details Main:', {
          marginTop: detailsStyles.marginTop,
          paddingTop: detailsStyles.paddingTop,
          top: detailsMain.getBoundingClientRect().top,
          layoutMarginTop: detailsLayoutStyles?.marginTop,
          layoutPaddingTop: detailsLayoutStyles?.paddingTop,
          contentMarginTop: detailsContentStyles?.marginTop,
          contentPaddingTop: detailsContentStyles?.paddingTop
        });
      }
      
      if (firstDetailCard) {
        const cardStyles = window.getComputedStyle(firstDetailCard);
        console.log('🔍 First Detail Card:', {
          marginTop: cardStyles.marginTop,
          paddingTop: cardStyles.paddingTop,
          top: firstDetailCard.getBoundingClientRect().top
        });
      }
      
      // Check for any elements between sections
      const summarySection = document.querySelector('.flight-summary-section');
      const detailsSection = document.querySelector('.flight-details-main');
      if (summarySection && detailsSection) {
        const summaryBottom = summarySection.getBoundingClientRect().bottom;
        const detailsTop = detailsSection.getBoundingClientRect().top;
        const sectionGap = detailsTop - summaryBottom;
        console.log('📏 Section Gap:', sectionGap + 'px');
      }
      
      // Calculate gap
      if (summaryCard && firstDetailCard) {
        const summaryBottom = summaryCard.getBoundingClientRect().bottom;
        const cardTop = firstDetailCard.getBoundingClientRect().top;
        const gap = cardTop - summaryBottom;
        console.log('📏 Gap between sections:', gap + 'px');
      }
    };
    
    // Check after a short delay to ensure DOM is ready
    setTimeout(checkSpacing, 100);
    window.addEventListener('resize', checkSpacing);
    
    return () => window.removeEventListener('resize', checkSpacing);
  }, []);
  const navigate = useNavigate();
  const [currency, setCurrency] = useState(null);

  // Initialize currency on component mount
  useEffect(() => {
    const initCurrency = async () => {
      try {
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
      } catch (error) {
        console.error('FlightDetailsPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
    };
    initCurrency();

    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      setCurrency(event.detail);
    };
    
    window.addEventListener('currencyInitialized', handleCurrencyInit);
    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  // Log all flight data for verification
  React.useEffect(() => {
    if (flight) {
      console.log('========================================');
      console.log('🛫 FLIGHT DETAILS PAGE - API DATA VERIFICATION');
      console.log('========================================');
      console.log('📋 Complete Flight Object:', flight);
      console.log('');
      console.log('✅ FROM API (Real Data):');
      console.log('  - Flight ID:', flight.id);
      console.log('  - Offer ID:', flight.offerId);
      console.log('  - Airline:', flight.airline);
      console.log('  - Flight Number:', flight.flightNumber);
      console.log('  - Departure Airport:', flight.departureAirport);
      console.log('  - Arrival Airport:', flight.arrivalAirport);
      console.log('  - Departure Time:', flight.departureTime);
      console.log('  - Arrival Time:', flight.arrivalTime);
      console.log('  - Duration:', flight.duration, flight.durationFormatted);
      console.log('  - Stops:', flight.stops);
      console.log('  - Price Total:', flight.price?.total || flight.total);
      console.log('  - Price Base:', flight.price?.base || flight.baseFare);
      console.log('  - Price Taxes:', flight.price?.taxes || flight.taxes);
      console.log('');
      console.log('📅 DATES (From API):');
      console.log('  - Departure Date (ISO):', flight.departureDate);
      console.log('  - Departure Date (Formatted):', flight.departureDateFormatted);
      console.log('  - Arrival Date (ISO):', flight.arrivalDate);
      console.log('  - Arrival Date (Formatted):', flight.arrivalDateFormatted);
      console.log('');
      console.log('✈️ AIRCRAFT & TERMINALS (From API):');
      console.log('  - Aircraft Type:', flight.aircraftType || '❌ NOT FROM API');
      console.log('  - Departure Terminal:', flight.departureTerminal || '❌ NOT FROM API');
      console.log('  - Arrival Terminal:', flight.arrivalTerminal || '❌ NOT FROM API');
      console.log('');
      console.log('🌍 CITIES (From API):');
      console.log('  - Departure City:', flight.departureCity || '❌ NOT FROM API (using fallback)');
      console.log('  - Arrival City:', flight.arrivalCity || '❌ NOT FROM API (using fallback)');
      console.log('');
      console.log('💺 CABIN & BAGGAGE (From API):');
      console.log('  - Cabin Class:', flight.cabinClass || flight.travelClass || '❌ NOT FROM API');
      console.log('  - Baggage Info:', flight.baggage || '❌ NOT FROM API');
      console.log('');
      console.log('📦 ADDITIONAL API DATA:');
      console.log('  - Number of Bookable Seats:', flight.numberOfBookableSeats || 'N/A');
      console.log('  - One Way:', flight.oneWay);
      console.log('  - Outbound Segments:', flight.outbound?.segments?.length || 0);
      console.log('  - Return Segments:', flight.return?.segments?.length || 0);
      console.log('');
      console.log('🔍 RAW OFFER DATA (Full API Response):');
      if (flight.rawOffer) {
        console.log('  - Raw Offer Available:', true);
        console.log('  - Raw Offer Keys:', Object.keys(flight.rawOffer));
        console.log('  - Raw Offer Sample:', {
          id: flight.rawOffer.id,
          total_amount: flight.rawOffer.total_amount,
          total_currency: flight.rawOffer.total_currency,
          base_amount: flight.rawOffer.base_amount,
          tax_amount: flight.rawOffer.tax_amount,
          cabin_class: flight.rawOffer.cabin_class,
          slices: flight.rawOffer.slices?.length || 0
        });
      } else {
        console.log('  - Raw Offer:', '❌ NOT AVAILABLE');
      }
      console.log('');
      console.log('❌ DUMMY/HARDCODED DATA (If any):');
      if (!flight.aircraftType) console.log('  ⚠️ Aircraft Type is missing from API');
      if (!flight.departureCity || flight.departureCity === 'City') console.log('  ⚠️ Departure City is using fallback');
      if (!flight.arrivalCity || flight.arrivalCity === 'City') console.log('  ⚠️ Arrival City is using fallback');
      if (!flight.cabinClass && !flight.travelClass) console.log('  ⚠️ Cabin Class is missing from API');
      if (!flight.baggage) console.log('  ⚠️ Baggage info is missing from API');
      console.log('========================================');
    } else {
      console.error('❌ No flight data found. Redirecting to flight search...');
      navigate('/results', { 
        replace: true,
        state: { error: 'Please select a flight first' }
      });
    }
  }, [flight, navigate]);

  const handleBookNow = () => {
    // CRITICAL: Use the current selectedClass state value directly
    // Normalize the selected class to ensure consistency
    let normalizedSelectedClass = selectedClass;
    if (typeof selectedClass === 'string') {
      normalizedSelectedClass = selectedClass.toLowerCase().trim().replace(/\s+/g, '_');
    }
    
    // Get the current class data for the SELECTED class (not the initial one)
    const selectedClassData = flight?.classPrices?.[normalizedSelectedClass];
    const selectedPrice = selectedClassData?.price?.total || selectedClassData?.price || selectedClassData;
    const selectedBaseFare = selectedClassData?.price?.base;
    const selectedTaxes = selectedClassData?.price?.taxes;
    // CRITICAL: Get the offer ID for the selected class, not the original flight's offer ID
    const selectedOfferId = selectedClassData?.offerId || selectedClassData?.id || flight?.id || flight?.offerId;
    
    console.log('🔵 ========== BOOK NOW CLICKED ==========');
    console.log('🔵 Selected class state value:', selectedClass);
    console.log('🔵 Normalized class:', normalizedSelectedClass);
    console.log('🔵 Selected class data:', selectedClassData);
    console.log('🔵 Selected price:', selectedPrice);
    console.log('🔵 Selected offer ID (CRITICAL):', selectedOfferId);
    console.log('🔵 Original flight offer ID:', flight?.id || flight?.offerId);
    console.log('🔵 Original flight travelClass:', flight?.travelClass);
    console.log('🔵 Original flight cabinClass:', flight?.cabinClass);
    
    // Create a completely new flight object, explicitly setting travelClass and cabinClass
    // Do NOT spread flight first - build it explicitly to avoid any property conflicts
    const updatedFlight = {
      // Copy all flight properties
      ...flight,
      // Override cities
      departureCity: flight.departureCity || (flight.departureAirport === 'LOS' ? 'Lagos' : 
                      flight.departureAirport === 'ABV' ? 'Abuja' : 
                      flight.departureAirport === 'LHR' ? 'London' : 
                      flight.departureAirport === 'DXB' ? 'Dubai' : 
                      flight.departureAirport === 'ACC' ? 'Accra' : 'City'),
      arrivalCity: flight.arrivalCity || (flight.arrivalAirport === 'LOS' ? 'Lagos' : 
                     flight.arrivalAirport === 'ABV' ? 'Abuja' : 
                     flight.arrivalAirport === 'LHR' ? 'London' : 
                     flight.arrivalAirport === 'DXB' ? 'Dubai' : 
                     flight.arrivalAirport === 'ACC' ? 'Accra' : 'City'),
    };
    
    // CRITICAL: Explicitly set travelClass and cabinClass AFTER creating the object
    // This ensures they override any existing values
    updatedFlight.travelClass = normalizedSelectedClass;
    updatedFlight.cabinClass = normalizedSelectedClass;
    
    // CRITICAL: Set the offer ID to the selected class's offer ID
    // This ensures we book the correct class (Business, not Economy)
    updatedFlight.id = selectedOfferId;
    updatedFlight.offerId = selectedOfferId;
    
    // Set price information for the selected class
    updatedFlight.price = selectedClassData?.price || flight.price || flight.priceObject || null;
    updatedFlight.baseFare = selectedBaseFare || flight.baseFare;
    updatedFlight.taxes = selectedTaxes || flight.taxes;
      const selectedPriceObj = selectedClassData?.price || flight.price || null;
      updatedFlight.total = selectedPriceObj
        ? formatFlightPrice(selectedPriceObj)
        : flight.total || flight.priceFormatted || null;
    
    console.log('🔵 Final flight object being passed:', {
      id: updatedFlight.id,
      offerId: updatedFlight.offerId,
      travelClass: updatedFlight.travelClass,
      cabinClass: updatedFlight.cabinClass,
      total: updatedFlight.total,
      price: updatedFlight.price
    });
    console.log('🔵 ========================================');
    
    // Navigate to passenger info page with flight data
    navigate('/passenger-info', { 
      state: { 
        flight: updatedFlight
      } 
    });
  };

  // Log what's being displayed on the page
  React.useEffect(() => {
    if (flight) {
      console.log('');
      console.log('📺 DISPLAYED ON PAGE:');
      console.log('  - Airline:', flight.airline);
      console.log('  - Aircraft Type (Displayed):', flight.aircraftType || '❌ NOT DISPLAYED (missing from API)');
      console.log('  - Price (Displayed):', flight.total || flight.price);
      console.log('  - Departure Time:', flight.departureTime);
      console.log('  - Departure Date (Displayed):', flight.departureDateFormatted || '❌ NOT DISPLAYED (using fallback)');
      console.log('  - Departure Airport:', flight.departureAirport);
      console.log('  - Departure Terminal (Displayed):', flight.departureTerminal || '❌ NOT DISPLAYED (missing from API)');
      console.log('  - Departure City (Displayed):', flight.departureCity || '❌ NOT DISPLAYED (using fallback)');
      console.log('  - Arrival Time:', flight.arrivalTime);
      console.log('  - Arrival Date (Displayed):', flight.arrivalDateFormatted || '❌ NOT DISPLAYED (using fallback)');
      console.log('  - Arrival Airport:', flight.arrivalAirport);
      console.log('  - Arrival Terminal (Displayed):', flight.arrivalTerminal || '❌ NOT DISPLAYED (missing from API)');
      console.log('  - Arrival City (Displayed):', flight.arrivalCity || '❌ NOT DISPLAYED (using fallback)');
      console.log('  - Duration:', flight.duration);
      console.log('  - Stops:', flight.stops);
      console.log('  - Travel Class (Displayed):', flight.travelClass || flight.cabinClass || '❌ NOT DISPLAYED (using fallback)');
      console.log('  - Base Fare (Displayed):', flight.baseFare || '❌ NOT DISPLAYED (missing from API)');
      console.log('  - Taxes (Displayed):', flight.taxes || '❌ NOT DISPLAYED (missing from API)');
      console.log('  - Baggage (Displayed):', flight.baggage ? JSON.stringify(flight.baggage) : '❌ NOT DISPLAYED (using fallback)');
      console.log('========================================');
    }
  }, [flight]);

  return (
    <div className="flight-details-page">
      <Header />
      
      {/* Flight Summary Card */}
      <section className="flight-summary-section">
        <div className="page-container">
          <div className="flight-summary-card">
            <div className="flight-summary-header">
              <div className="flight-airline-info">
                <span className="flight-airline-logo-large">{flight?.airlineLogo}</span>
                <div>
                  <h2 className="flight-airline-name-large">{flight?.airline}</h2>
                  <p className="flight-aircraft-type">{flight?.aircraftType || 'Aircraft information not available'}</p>
                </div>
              </div>
              <div className="flight-price-summary">
                <span className="price-label">Total Price</span>
                <span className="price-value-large">
                  {(() => {
                    if (!currency) return 'Loading...';
                    // Prefer full price object (with originalCurrency) so conversion matches API exactly
                    if (currentPriceObj && typeof currentPriceObj === 'object') {
                      return formatFlightPrice(currentPriceObj);
                    }
                    // Fallback if we only have a raw number
                    const priceValue = typeof currentPrice === 'number' ? currentPrice : parseFloat(currentPrice || 0);
                    return formatPrice(priceValue);
                  })()}
                </span>
              </div>
            </div>

            {/* Multi-City Routes */}
            {flight.isMultiCity && flight.slices && flight.slices.length > 0 && (
              <>
                {flight.slices.map((slice, sliceIndex) => {
                  const firstSegment = slice.segments?.[0];
                  const lastSegment = slice.segments?.[slice.segments?.length - 1];
                  
                  const formatTime = (isoString) => {
                    if (!isoString) return '';
                    try {
                      const date = new Date(isoString);
                      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                    } catch {
                      return '';
                    }
                  };
                  
                  const formatDate = (isoString) => {
                    if (!isoString) return 'Date';
                    try {
                      const date = new Date(isoString);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    } catch {
                      return 'Date';
                    }
                  };
                  
                  const formatDuration = (duration) => {
                    if (!duration) return 'N/A';
                    if (typeof duration === 'string' && duration.startsWith('PT')) {
                      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                      if (match) {
                        const hours = match[1] || '0';
                        const minutes = match[2] || '0';
                        if (hours === '0') return `${minutes}m`;
                        if (minutes === '0') return `${hours}h`;
                        return `${hours}h ${minutes}m`;
                      }
                    }
                    return duration;
                  };
                  
                  const getCityName = (airportCode) => {
                    const cityMap = {
                      'LOS': 'Lagos', 'ABV': 'Abuja', 'LHR': 'London', 'DXB': 'Dubai',
                      'ACC': 'Accra', 'CDG': 'Paris', 'JFK': 'New York', 'LAX': 'Los Angeles',
                      'NBO': 'Nairobi', 'CPT': 'Cape Town', 'CAI': 'Cairo', 'ADD': 'Addis Ababa'
                    };
                    return cityMap[airportCode] || 'City';
                  };
                  
                  if (!firstSegment || !lastSegment) return null;
                  
                  const originCode = firstSegment.origin?.iata_code || '';
                  const destCode = lastSegment.destination?.iata_code || '';
                  const stops = slice.segments ? (slice.segments.length - 1) : 0;
                  
                  return (
                    <div key={sliceIndex} className="flight-leg-section" style={{ marginBottom: sliceIndex < flight.slices.length - 1 ? '20px' : '0' }}>
                      <div className="flight-leg-header">
                        <span className="flight-leg-label">Route {sliceIndex + 1} of {flight.slices.length}</span>
                      </div>
                      <div className="flight-route-details">
                        <div className="route-segment">
                          <div className="route-time-block">
                            <span className="route-time-large">{formatTime(firstSegment.departing_at)}</span>
                            <span className="route-date">{formatDate(firstSegment.departing_at)}</span>
                          </div>
                          <div className="route-airport-block">
                            <span className="route-airport-code">{originCode}</span>
                            {firstSegment.origin_terminal && <span className="route-terminal">Terminal {firstSegment.origin_terminal}</span>}
                            <span className="route-city">{firstSegment.origin?.city_name || getCityName(originCode)}</span>
                          </div>
                        </div>

                        <div className="route-connector">
                          <div className="route-duration">{formatDuration(slice.duration)}</div>
                          <div className="route-line-vertical">
                            <span className="route-icon-small">✈️</span>
                          </div>
                          <div className="route-stops-block">
                            <div className="route-stops">{stops === 0 ? 'Direct' : stops === 1 ? '1 stop' : `${stops} stops`}</div>
                            {(() => {
                              const segs = slice.segments;
                              if (!segs || segs.length < 2) return null;
                              const stopList = segs.slice(0, -1).map((seg) => {
                                const code = seg.destination?.iata_code || seg.arrival?.airport;
                                if (!code) return null;
                                const c = typeof code === 'string' ? code : code.iata_code;
                                return seg.destination?.city_name || getCityNameFromCode(c);
                              }).filter(Boolean);
                              if (stopList.length === 0) return null;
                              return <div className="route-stops-where">Stop in: {stopList.join(', ')}</div>;
                            })()}
                          </div>
                        </div>

                        <div className="route-segment">
                          <div className="route-time-block">
                            <span className="route-time-large">{formatTime(lastSegment.arriving_at)}</span>
                            <span className="route-date">{formatDate(lastSegment.arriving_at)}</span>
                          </div>
                          <div className="route-airport-block">
                            <span className="route-airport-code">{destCode}</span>
                            {lastSegment.destination_terminal && <span className="route-terminal">Terminal {lastSegment.destination_terminal}</span>}
                            <span className="route-city">{lastSegment.destination?.city_name || getCityName(destCode)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Outbound Flight (One-way or Round-trip) */}
            {!flight.isMultiCity && (
              <div className="flight-leg-section">
                <div className="flight-leg-header">
                  <span className="flight-leg-label">Outbound</span>
                </div>
                <div className="flight-route-details">
                  <div className="route-segment">
                    <div className="route-time-block">
                      <span className="route-time-large">{flight.departureTime}</span>
                      <span className="route-date">{flight.departureDateFormatted || 'Date'}</span>
                    </div>
                    <div className="route-airport-block">
                      <span className="route-airport-code">{flight.departureAirport}</span>
                      {flight.departureTerminal && <span className="route-terminal">Terminal {flight.departureTerminal}</span>}
                      <span className="route-city">{flight.departureCity || 'City'}</span>
                    </div>
                  </div>

                  <div className="route-connector">
                    <div className="route-duration">{flight.duration}</div>
                    <div className="route-line-vertical">
                      <span className="route-icon-small">✈️</span>
                    </div>
                    <div className="route-stops-block">
                      <div className="route-stops">{stopsLabel(flight.stops)}</div>
                      {(() => {
                        const stopAirports = getStopAirportsFromSegments(flight.outbound?.segments);
                        if (stopAirports.length === 0) return null;
                        return (
                          <div className="route-stops-where">
                            Stop in: {stopAirports.map((s) => s.city).join(', ')}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="route-segment">
                    <div className="route-time-block">
                      <span className="route-time-large">{flight.arrivalTime}</span>
                      <span className="route-date">{flight.arrivalDateFormatted || 'Date'}</span>
                    </div>
                    <div className="route-airport-block">
                      <span className="route-airport-code">{flight.arrivalAirport}</span>
                      {flight.arrivalTerminal && <span className="route-terminal">Terminal {flight.arrivalTerminal}</span>}
                      <span className="route-city">{flight.arrivalCity || 'City'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Return Flight (if round-trip, NOT multi-city) */}
            {flight.isRoundTrip && !flight.isMultiCity && flight.returnFlight && (
              <div className="flight-leg-section return-leg-section">
                <div className="flight-leg-header">
                  <span className="flight-leg-label return-label">Return</span>
                </div>
                <div className="flight-route-details">
                  <div className="route-segment">
                    <div className="route-time-block">
                      <span className="route-time-large">{flight.returnFlight.departureTime}</span>
                      <span className="route-date">
                        {(() => {
                          // Format return departure date
                          if (flight.return?.segments?.[0]?.departing_at) {
                            const date = new Date(flight.return.segments[0].departing_at);
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          }
                          return 'Date';
                        })()}
                      </span>
                    </div>
                    <div className="route-airport-block">
                      <span className="route-airport-code">{flight.returnFlight.departureAirport}</span>
                      {flight.return?.segments?.[0]?.origin_terminal && (
                        <span className="route-terminal">Terminal {flight.return.segments[0].origin_terminal}</span>
                      )}
                      <span className="route-city">
                        {flight.returnFlight.departureAirport === 'LOS' ? 'Lagos' : 
                         flight.returnFlight.departureAirport === 'ABV' ? 'Abuja' : 
                         flight.returnFlight.departureAirport === 'LHR' ? 'London' : 
                         flight.returnFlight.departureAirport === 'DXB' ? 'Dubai' : 
                         flight.returnFlight.departureAirport === 'ACC' ? 'Accra' : 
                         flight.returnFlight.departureAirport === 'CDG' ? 'Paris' : 'City'}
                      </span>
                    </div>
                  </div>

                  <div className="route-connector">
                    <div className="route-duration">{flight.returnFlight.duration}</div>
                    <div className="route-line-vertical">
                      <span className="route-icon-small">✈️</span>
                    </div>
                    <div className="route-stops-block">
                      <div className="route-stops">{stopsLabel(flight.returnFlight.stops)}</div>
                      {(() => {
                        const stopAirports = getStopAirportsFromSegments(flight.return?.segments);
                        if (stopAirports.length === 0) return null;
                        return (
                          <div className="route-stops-where">
                            Stop in: {stopAirports.map((s) => s.city).join(', ')}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="route-segment">
                    <div className="route-time-block">
                      <span className="route-time-large">{flight.returnFlight.arrivalTime}</span>
                      <span className="route-date">
                        {(() => {
                          // Format return arrival date
                          if (flight.return?.segments) {
                            const lastSegment = flight.return.segments[flight.return.segments.length - 1];
                            if (lastSegment?.arriving_at) {
                              const date = new Date(lastSegment.arriving_at);
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            }
                          }
                          return 'Date';
                        })()}
                      </span>
                    </div>
                    <div className="route-airport-block">
                      <span className="route-airport-code">{flight.returnFlight.arrivalAirport}</span>
                      {flight.return?.segments && flight.return.segments.length > 0 && flight.return.segments[flight.return.segments.length - 1]?.destination_terminal && (
                        <span className="route-terminal">Terminal {flight.return.segments[flight.return.segments.length - 1].destination_terminal}</span>
                      )}
                      <span className="route-city">
                        {flight.returnFlight.arrivalAirport === 'LOS' ? 'Lagos' : 
                         flight.returnFlight.arrivalAirport === 'ABV' ? 'Abuja' : 
                         flight.returnFlight.arrivalAirport === 'LHR' ? 'London' : 
                         flight.returnFlight.arrivalAirport === 'DXB' ? 'Dubai' : 
                         flight.returnFlight.arrivalAirport === 'ACC' ? 'Accra' : 
                         flight.returnFlight.arrivalAirport === 'CDG' ? 'Paris' : 'City'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flight-class-badge">
              <span className="class-label">Travel Class:</span>
              <span className="class-value">{formatClassForDisplay(selectedClass)}</span>
            </div>
            {flight.aircraftType && (
              <div className="flight-aircraft-info">
                <span className="aircraft-label">Aircraft:</span>
                <span className="aircraft-value">{flight.aircraftType}</span>
              </div>
            )}
          </div>

          {/* Right Side - Price Breakdown (moved to top) */}
          <aside className="price-breakdown-panel">
            <div className="price-breakdown-card">
              <h3 className="price-breakdown-title">Price Breakdown</h3>
                        
              {(() => {
                // Helper to get a display-ready amount without double conversion
                const getDisplayAmount = (formattedValue, rawValue) => {
                  // If we already have a formatted string (from results/passenger pages), use it directly
                  if (typeof formattedValue === 'string' && formattedValue.trim() !== '') {
                    return formattedValue;
                  }

                  // Otherwise, fall back to raw numeric value from API and format once
                  if (rawValue !== null && rawValue !== undefined && currency) {
                    const num = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
                    if (!isNaN(num)) {
                      const basePriceObj = {
                        total: num,
                        originalCurrency: flight?.price?.originalCurrency || flight?.price?.currency || currency.code,
                        originalTotal: num
                      };
                      return formatFlightPrice(basePriceObj);
                    }
                  }

                  return null;
                };

                const baseFareDisplay = getDisplayAmount(
                  currentBaseFare || flight.baseFare,
                  currentClassData?.price?.base || flight.price?.base
                );

                const taxesDisplay = getDisplayAmount(
                  currentTaxes || flight.taxes,
                  currentClassData?.price?.taxes || flight.price?.taxes
                );

                return (
                  <>
                    {baseFareDisplay && (
                      <div className="price-item">
                        <div className="price-item-label">
                          <span className="price-icon">💰</span>
                          <span>Base Fare</span>
                        </div>
                        <span className="price-item-value">{baseFareDisplay}</span>
                      </div>
                    )}

                    {taxesDisplay && (
                      <div className="price-item">
                        <div className="price-item-label">
                          <span className="price-icon">📋</span>
                          <span>Taxes & Fees</span>
                        </div>
                        <span className="price-item-value">{taxesDisplay}</span>
                      </div>
                    )}

                    <div className="price-divider"></div>

                    <div className="price-item total">
                      <div className="price-item-label">
                        <span className="price-icon">💳</span>
                        <span>Total</span>
                      </div>
                      <span className="price-item-value">
                        {(() => {
                          if (!currency) return 'Loading...';
                          if (currentPriceObj && typeof currentPriceObj === 'object') {
                            return formatFlightPrice(currentPriceObj);
                          }
                          const priceValue = typeof currentPrice === 'number' ? currentPrice : parseFloat(currentPrice || 0);
                          return formatPrice(priceValue);
                        })()}
                      </span>
                    </div>
                  </>
                );
              })()}

              <button className="book-now-button" onClick={handleBookNow}>
                Book Now
              </button>

              <p className="price-note">
                {currency 
                  ? `Prices are in ${currency.name} (${currency.symbol}). Final price may vary based on seat selection.`
                  : 'Prices are displayed in your local currency. Final price may vary based on seat selection.'}
              </p>
            </div>

            {/* Class Selector Section */}
            {flight.classPrices && Object.keys(flight.classPrices).length > 0 && (
              <div className="price-breakdown-card class-prices-card">
                <h3 className="price-breakdown-title">Select Travel Class</h3>
                <div className="class-prices-list">
                  {Object.entries(flight.classPrices).map(([cabinClass, classData]: [string, any]) => {
                    const classLabels: Record<string, string> = {
                      'economy': 'Economy',
                      'premium_economy': 'Premium Economy',
                      'business': 'Business',
                      'first': 'First Class'
                    };
                    
                    const classLabel = classLabels[cabinClass] || cabinClass;
                    const classPrice = classData.price?.total || classData.price || classData;
                    const priceValue = typeof classPrice === 'number' ? classPrice : parseFloat(classPrice || 0);
                    const isSelected = selectedClass === cabinClass;
                    
                    return (
                      <div 
                        key={cabinClass} 
                        className={`class-price-item-details ${isSelected ? 'selected-class' : ''}`}
                        onClick={() => {
                          console.log('🔵 Class clicked:', cabinClass);
                          console.log('🔵 Current selectedClass before update:', selectedClass);
                          setSelectedClass(cabinClass);
                          console.log('🔵 Setting selectedClass to:', cabinClass);
                        }}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                          border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                          borderRadius: '8px',
                          padding: '12px',
                          transition: 'all 0.2s ease',
                          marginBottom: '8px'
                        }}
                      >
                        <div className="class-price-header-details">
                          <span className="class-label-details" style={{ fontWeight: isSelected ? '600' : '400' }}>
                            {classLabel} {isSelected && '✓'}
                          </span>
                          <span className="class-price-value-details" style={{ fontWeight: isSelected ? '600' : '400' }}>
                            {currency
                              ? (classData.price
                                  ? formatFlightPrice(classData.price)
                                  : formatPrice(priceValue))
                              : 'Loading...'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="flight-details-main">
        <div className="page-container">
          <div className="details-layout">
            {/* Left Side - Flight Details */}
            <main className="flight-details-content">
              {/* Baggage Allowance */}
              <div className="detail-card">
                <div className="detail-card-header">
                  <span className="detail-icon">🧳</span>
                  <h3 className="detail-title">Baggage Allowance</h3>
                </div>
                <div className="detail-card-body">
                  {(() => {
                    // Get airline code from flight data - try multiple sources
                    let airlineCode = flight?.airlineCode || 
                                     flight?.carrierCode || 
                                     flight?.segments?.[0]?.carrier?.iata_code ||
                                     flight?.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code ||
                                     flight?.outbound?.segments?.[0]?.marketing_carrier?.iata_code ||
                                     null;
                    
                    // If no code found, try to extract from airline name
                    if (!airlineCode && flight?.airline) {
                      airlineCode = getAirlineCodeFromName(flight.airline);
                    }
                    
                    // Fallback to DEFAULT if still no code
                    if (!airlineCode) {
                      airlineCode = 'DEFAULT';
                    }
                    
                    console.log('🔍 Baggage Detection:', {
                      airlineName: flight?.airline,
                      airlineCode: airlineCode,
                      availableCodes: Object.keys(airlineBaggageDatabase),
                      flightNumber: flight?.flightNumber,
                      carrierCode: flight?.carrierCode,
                      segments: flight?.segments?.[0]?.carrier?.iata_code,
                      slices: flight?.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code,
                      outbound: flight?.outbound?.segments?.[0]?.marketing_carrier?.iata_code
                    });
                    
                    // Get all available classes from flight data, or default to all classes
                    const availableBaggageClasses = flight?.classPrices && Object.keys(flight.classPrices).length > 0
                      ? Object.keys(flight.classPrices)
                      : ['economy', 'premium_economy', 'business', 'first'];
                    
                    // Filter to only show classes that have baggage data available
                    const baggageClasses = availableBaggageClasses.filter(cabinClass => {
                      const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                      return baggageInfo !== null;
                    });
                    
                    // If no classes found, show at least economy and business
                    const finalBaggageClasses = baggageClasses.length > 0 
                      ? baggageClasses 
                      : ['economy', 'business'];
                    
                    console.log('📦 Baggage Info for Classes:', finalBaggageClasses.map(cabinClass => {
                      const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                      const formattedBaggage = formatBaggageForDisplay(baggageInfo);
                      return {
                        class: cabinClass,
                        airlineCode: airlineCode,
                        airlineName: baggageInfo?.airlineName,
                        baggageInfo: baggageInfo,
                        formatted: formattedBaggage
                      };
                    }));
                    
                    // Show baggage for all available classes
                    return finalBaggageClasses.map((cabinClass) => {
                      const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                      const formattedBaggage = formatBaggageForDisplay(baggageInfo);
                      
                      console.log(`📦 Baggage for ${cabinClass} class:`, {
                        airlineCode: airlineCode,
                        airlineName: baggageInfo?.airlineName,
                        class: baggageInfo?.class,
                        carryOn: baggageInfo?.carryOn,
                        checked: baggageInfo?.checked,
                        formatted: formattedBaggage
                      });
                      
                      if (!formattedBaggage) return null;
                      
                      const classLabel = formatClassForDisplay(cabinClass);
                      
                      return (
                        <div key={cabinClass} className="baggage-class-section">
                          <div className="baggage-class-header">
                            <span className="baggage-class-name">{classLabel}</span>
                          </div>
                          <div className="baggage-class-items">
                            <div className="baggage-item">
                              <span className="baggage-label">Carry-on Baggage</span>
                              <span className="baggage-value">{formattedBaggage.carryOn}</span>
                            </div>
                            <div className="baggage-item">
                              <span className="baggage-label">Checked Baggage</span>
                              <span className="baggage-value">{formattedBaggage.checked}</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Cabin Amenities */}
              <div className="detail-card">
                <div className="detail-card-header">
                  <span className="detail-icon">✨</span>
                  <h3 className="detail-title">Cabin Amenities</h3>
                </div>
                <div className="detail-card-body">
                  <div className="amenities-grid">
                    {flight.amenities?.map((amenity, idx) => (
                      <div key={idx} className="amenity-item">
                        <span className="amenity-icon">
                          {amenity === 'WiFi' && '📶'}
                          {amenity === 'Lounge' && '🏛️'}
                          {amenity === 'Meals' && '🍽️'}
                          {amenity === 'Lie-flat Seat' && '🛋️'}
                          {amenity === 'Entertainment' && '🎬'}
                          {!['WiFi', 'Lounge', 'Meals', 'Lie-flat Seat', 'Entertainment'].includes(amenity) && '✓'}
                        </span>
                        <span className="amenity-name">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Flight Highlights */}
              <div className="detail-card">
                <div className="detail-card-header">
                  <span className="detail-icon">⭐</span>
                  <h3 className="detail-title">Flight Highlights</h3>
                </div>
                <div className="detail-card-body">
                  <div className="highlights-list">
                    {/* Stops Information */}
                    {flight.stops !== undefined && (
                      <div className="highlight-item">
                        <span className="highlight-badge">
                          {flight.stops === 0 ? 'Non-stop' : `${flight.stops} Stop${flight.stops > 1 ? 's' : ''}`}
                        </span>
                        <span className="highlight-text">
                          {flight.stops === 0 
                            ? 'Direct flight with no layovers' 
                            : `Flight with ${flight.stops} stop${flight.stops > 1 ? 's' : ''} en route`}
                        </span>
                      </div>
                    )}
                    
                    {/* Duration Information */}
                    {flight.duration && (
                      <div className="highlight-item">
                        <span className="highlight-badge">Duration</span>
                        <span className="highlight-text">Total flight time: {flight.duration}</span>
                      </div>
                    )}
                    
                    {/* Aircraft Type */}
                    {flight.aircraftType && (
                      <div className="highlight-item">
                        <span className="highlight-badge">Aircraft</span>
                        <span className="highlight-text">{flight.aircraftType}</span>
                      </div>
                    )}
                    
                    {/* Flight Number */}
                    {flight.flightNumber && (
                      <div className="highlight-item">
                        <span className="highlight-badge">Flight Number</span>
                        <span className="highlight-text">{flight.flightNumber}</span>
                      </div>
                    )}
                    
                    {/* Travel Class */}
                    <div className="highlight-item">
                      <span className="highlight-badge">{formatClassForDisplay(selectedClass)} Class</span>
                      <span className="highlight-text">
                        {selectedClass === 'business' || selectedClass === 'first' 
                          ? 'Premium seating and enhanced service' 
                          : 'Standard seating and service'}
                      </span>
                    </div>
                    
                    {/* Amenities if available */}
                    {flight.amenities && flight.amenities.length > 0 && (
                      <div className="highlight-item">
                        <span className="highlight-badge">Amenities</span>
                        <span className="highlight-text">{flight.amenities.join(', ')}</span>
                      </div>
                    )}
                    
                    {/* Airline Information */}
                    {flight.airline && (
                      <div className="highlight-item">
                        <span className="highlight-badge">Airline</span>
                        <span className="highlight-text">{flight.airline}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FlightDetailsPage;

